import { useEffect } from 'react';
import { useParams, Outlet } from 'react-router-dom';
import { useCampaignStore } from '../stores/campaignStore';
import { AlertCircle, Gift } from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';

/**
 * Wrapper route that loads a campaign by slug.
 * Renders children (Outlet) once the campaign is ready.
 */
export function CampaignLoader() {
  const { campaignSlug } = useParams<{ campaignSlug: string }>();
  const { campaign, status, error, loadCampaignBySlug } = useCampaignStore();
  const { trackException, trackSelectContent } = useAnalytics();

  useEffect(() => {
    if (campaignSlug) {
      loadCampaignBySlug(campaignSlug);
    }
  }, [campaignSlug, loadCampaignBySlug]);

  useEffect(() => {
    if (status === 'error') trackException('campaign_load_failed');
    if (status === 'not-found') trackException('campaign_not_found');
  }, [status, trackException]);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-rchlo-green mb-4" />
        <p className="text-sm text-gray-500">Carregando campanha...</p>
      </div>
    );
  }

  if (status === 'not-found') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6">
        <div className="card max-w-md w-full text-center py-10">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Campanha não encontrada</h2>
          <p className="text-sm text-gray-500">{error}</p>
          <a href="/" className="text-sm text-rchlo-green hover:underline mt-4 inline-block">
            Voltar ao início
          </a>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6">
        <div className="card max-w-md w-full text-center py-10">
          <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-orange-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Erro ao carregar</h2>
          <p className="text-sm text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => {
              trackSelectContent('button', 'recarregar_campanha');
              if (campaignSlug) loadCampaignBySlug(campaignSlug);
            }}
            className="btn-primary"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // Campaign scheduled — show "coming soon" state
  // Also show if campaign is Active but reservation period hasn't started yet
  const isBeforeReservationStart = campaign?.status === 'Active'
    && campaign.startDate
    && new Date(`${campaign.startDate}T00:00:00`) > new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00');

  if (campaign?.status === 'Scheduled' || isBeforeReservationStart) {
    const startDateFormatted = campaign.startDate
      ? new Date(`${campaign.startDate}T12:00:00`).toLocaleDateString('pt-BR')
      : '';

    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <header className="relative z-30 flex items-center justify-center bg-rchlo-green px-5 py-3">
          <span className="text-lg font-bold uppercase tracking-widest text-white">Riachuelo</span>
        </header>

        {campaign.initialImageUrl && (
          <img
            src={campaign.initialImageUrl}
            alt={campaign.name}
            className="w-full object-cover max-h-[55vh]"
          />
        )}

        <div className="flex flex-col items-center px-6 py-10 text-center">
          {campaign.productImage && (
            <img
              src={campaign.productImage}
              alt={campaign.productName}
              className="h-20 object-contain mb-6"
            />
          )}
          <h1 className="text-xl font-bold text-rchlo-green leading-snug">
            Aguarde! Esta campanha iniciará em<br />{startDateFormatted}.
          </h1>
          <p className="text-sm text-gray-500 mt-3 max-w-xs">
            Em breve você poderá se cadastrar e retirar seu brinde exclusivo nas lojas Riachuelo.
          </p>
        </div>
      </div>
    );
  }

  // Campaign finished — show ended state
  if (campaign?.status === 'Finished') {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <header className="relative z-30 flex items-center justify-center bg-rchlo-green px-5 py-3">
          <span className="text-lg font-bold uppercase tracking-widest text-white">Riachuelo</span>
        </header>

        {/* Campaign image with dark overlay */}
        {campaign.initialImageUrl && (
          <div className="relative">
            <img
              src={campaign.initialImageUrl}
              alt={campaign.name}
              className="w-full object-cover max-h-[55vh] brightness-[0.6]"
            />
          </div>
        )}

        {/* Ended message */}
        <div className="flex flex-col items-center px-6 py-10 text-center">
          {campaign.productImage && (
            <img
              src={campaign.productImage}
              alt={campaign.productName}
              className="h-20 object-contain mb-6 opacity-70"
            />
          )}
          <h1 className="text-xl font-bold text-rchlo-green leading-snug">
            Que pena! Infelizmente essa campanha<br />já está encerrada.
          </h1>
          <p className="text-sm text-gray-500 mt-3 max-w-xs">
            Fique de olho nas próximas campanhas da Riachuelo e não perca as novidades.
          </p>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
